import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useText } from "../context/ContentContext";

const PROPERTY_TYPES = [
    "Residential (Primary)",
    "Residential (Vacation)",
    "Residential (Investment)",
    "Commercial",
    "Industrial",
];

const PROVINCES = [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Nova Scotia",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
    "Northwest Territories",
    "Nunavut",
    "Yukon",
];

const CALENDLY_CSS = "https://assets.calendly.com/assets/external/widget.css";
const CALENDLY_JS = "https://assets.calendly.com/assets/external/widget.js";

function ensureCalendlyLoaded() {
    return new Promise((resolve) => {
        if (typeof window === "undefined") return resolve(false);
        if (window.Calendly) return resolve(true);

        if (!document.querySelector(`link[href="${CALENDLY_CSS}"]`)) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = CALENDLY_CSS;
            document.head.appendChild(link);
        }

        let script = document.querySelector(`script[src="${CALENDLY_JS}"]`);
        if (!script) {
            script = document.createElement("script");
            script.src = CALENDLY_JS;
            script.async = true;
            document.body.appendChild(script);
        }
        script.addEventListener("load", () => resolve(!!window.Calendly));
        if (window.Calendly) resolve(true);
    });
}

export default function GetStartedPage() {
    const calendlyUrl = useText("calendly_url", "https://calendly.com/callthom/realtorreferral");
    const consentDisclaimer = useText(
        "consent_disclaimer",
        "RealtorReferral.ca is operated by Thom Gallagher, Sales Representative, iCloud Realty Ltd. We collect the information on this form so Thom can personally match you with up to three vetted real estate agents in your target market. Your details will be shared only with those agents — never sold, rented, or traded to third-party lead-buyers or advertisers. Your information is stored securely and retained for up to 24 months after our last contact unless you ask us to delete it sooner. You may access, correct, or delete your information at any time, and withdraw your consent at any time, by emailing privacy@realtorreferral.ca. Full details are available in our Privacy Policy and Terms of Service."
    );
    const [searchParams] = useSearchParams();
    const destProvinceParam = searchParams.get("dest_province") || "";
    const searchLocationParam = searchParams.get("search_location") || destProvinceParam;
    const sourceParam = searchParams.get("source") || "";
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submittedData, setSubmittedData] = useState(null);
    const [consentMatching, setConsentMatching] = useState(false);
    const [consentMarketing, setConsentMarketing] = useState(false);

    // Pre-load Calendly script as soon as the user lands on the form
    useEffect(() => {
        ensureCalendlyLoaded();
    }, []);

    const openCalendlyPopup = async (lead) => {
        const ok = await ensureCalendlyLoaded();
        if (!ok || !window.Calendly || !calendlyUrl) {
            toast.error("Could not open the booking widget. Please try again.");
            return;
        }
        window.Calendly.initPopupWidget({
            url: calendlyUrl,
            prefill: lead
                ? {
                      name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
                      email: lead.email || "",
                  }
                : undefined,
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const payload = Object.fromEntries(fd.entries());
        if (!payload.first_name || !payload.last_name || !payload.email || !payload.phone) {
            toast.error("Please fill in the required fields.");
            return;
        }
        if (!consentMatching) {
            toast.error("Please confirm consent to the matching service before submitting.");
            return;
        }
        payload.consent_matching = consentMatching;
        payload.consent_marketing = consentMarketing;
        if (sourceParam) payload.source = sourceParam;
        setSubmitting(true);
        try {
            await api.post("/leads", payload);
            setSubmittedData(payload);
            setSubmitted(true);
            toast.success("Thanks! Pick a time that works for your intro call.");
            setTimeout(() => openCalendlyPopup(payload), 350);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="bg-brand-paper min-h-[70vh] flex items-center justify-center px-6 py-24" data-testid="get-started-success">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-xl text-center"
                >
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                        — Submission received
                    </div>
                    <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-ink">
                        Thank <span className="italic">you</span>
                    </h1>
                    <p className="mt-8 text-lg text-brand-muted leading-relaxed">
                        Your details are with Thom personally. The next step is a short
                        video call so we can get the match right — pick a time that
                        works for you below.
                    </p>
                    <p className="mt-4 text-sm text-brand-ink leading-relaxed" data-testid="get-started-sla">
                        You'll hear back from Thom <span className="font-semibold">within 3 business days</span>.
                    </p>
                    <button
                        onClick={() => openCalendlyPopup(submittedData)}
                        data-testid="open-calendly-button"
                        className="mt-10 inline-flex items-center gap-3 bg-brand-ink text-brand-paper px-8 py-4 text-sm tracking-wide hover:bg-black transition-all hover:gap-4"
                    >
                        Book your intro call
                        <span aria-hidden>→</span>
                    </button>
                    <p className="mt-6 text-xs text-brand-muted">
                        Didn't see the booking window? Click the button above to reopen it.
                    </p>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="bg-brand-paper" data-testid="get-started-page">
            <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-3xl"
                >
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                        — Step 01 · Intake
                    </div>
                    <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight text-brand-ink">
                        Let's find your <span className="italic">match</span>
                    </h1>
                    <p className="mt-8 text-lg text-brand-muted max-w-2xl leading-relaxed">
                        A few details help us shortlist the right agents. Required fields
                        are marked with an asterisk. After you submit, you'll be invited
                        to book a short video call with Thom — right on this page.
                    </p>
                    {destProvinceParam && (
                        <div
                            data-testid="dest-province-banner"
                            className="mt-8 inline-flex items-center gap-3 border border-brand-gold/50 bg-brand-sand/60 px-5 py-3 text-sm text-brand-ink"
                        >
                            <span className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                                Match target
                            </span>
                            <span className="text-brand-ink">
                                {searchLocationParam || destProvinceParam}
                            </span>
                        </div>
                    )}
                </motion.div>
            </section>

            <section className="mx-auto max-w-4xl px-6 lg:px-10 pb-24 lg:pb-32">
                <form
                    onSubmit={onSubmit}
                    className="border border-brand-line bg-brand-paper p-8 lg:p-12 space-y-10"
                    data-testid="lead-form"
                >
                    <FieldGroup label="Your details">
                        <div className="grid sm:grid-cols-2 gap-6">
                            <Input name="first_name" label="First name *" required testId="lead-first-name" />
                            <Input name="last_name" label="Last name *" required testId="lead-last-name" />
                            <Input name="email" type="email" label="Email *" required testId="lead-email" />
                            <Input name="phone" label="Mobile phone *" required testId="lead-phone" />
                        </div>
                    </FieldGroup>

                    <FieldGroup label="About you">
                        <div className="grid sm:grid-cols-2 gap-6">
                            <Select
                                name="working_with_agent"
                                label="Currently working with a licensed agent?"
                                options={["No", "Yes"]}
                                testId="lead-working-with-agent"
                            />
                            <Input name="city" label="Your current city" testId="lead-city" />
                            <Select name="province" label="Your province" options={PROVINCES} testId="lead-province" />
                            <Input name="postal_code" label="Postal code" testId="lead-postal" />
                        </div>
                    </FieldGroup>

                    <FieldGroup label="What you're searching for">
                        <div className="grid sm:grid-cols-2 gap-6">
                            <Select name="property_type" label="Property type" options={PROPERTY_TYPES} testId="lead-property-type" />
                            <Input name="search_location" label="Primary search location" testId="lead-search-location" defaultValue={searchLocationParam} />
                        </div>
                        <Textarea
                            name="search_criteria"
                            label="Specific search criteria"
                            placeholder="Beds, baths, budget range, must-haves, dealbreakers…"
                            testId="lead-search-criteria"
                        />
                    </FieldGroup>

                    <FieldGroup label="Your ideal agent">
                        <Textarea
                            name="agent_skills"
                            label="Important skills in an agent"
                            placeholder="E.g. strong negotiator, fluent in another language, deep local network…"
                            testId="lead-agent-skills"
                        />
                        <Textarea
                            name="matching_notes"
                            label="How can we make matching most effective?"
                            placeholder="Anything else we should know about you, your timeline, or your situation."
                            testId="lead-matching-notes"
                        />
                    </FieldGroup>

                    <FieldGroup label="Privacy & consent">
                        <div className="bg-brand-sand/60 border border-brand-line p-5 text-sm text-brand-ink/85 leading-relaxed">
                            <p className="mb-3">{consentDisclaimer}</p>
                            <p className="text-brand-muted">
                                Read the full{" "}
                                <Link to="/privacy-policy" target="_blank" rel="noreferrer" className="text-brand-ink underline">
                                    Privacy Policy
                                </Link>{" "}
                                and{" "}
                                <Link to="/terms" target="_blank" rel="noreferrer" className="text-brand-ink underline">
                                    Terms of Service
                                </Link>
                                .
                            </p>
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-matching-label">
                            <input
                                type="checkbox"
                                checked={consentMatching}
                                onChange={(e) => setConsentMatching(e.target.checked)}
                                required
                                data-testid="consent-matching-checkbox"
                                className="mt-1.5 w-4 h-4 accent-brand-ink"
                            />
                            <span className="text-sm text-brand-ink leading-relaxed">
                                <strong>Required.</strong> I have read and understand the Privacy Notice
                                above. I consent to RealtorReferral.ca (Thom Gallagher, iCloud Realty Ltd)
                                collecting, using, and disclosing my personal information to vetted real
                                estate agents for the sole purpose of matching me with an agent who fits
                                my needs. I understand I can withdraw this consent at any time by emailing
                                privacy@realtorreferral.ca.
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-marketing-label">
                            <input
                                type="checkbox"
                                checked={consentMarketing}
                                onChange={(e) => setConsentMarketing(e.target.checked)}
                                data-testid="consent-marketing-checkbox"
                                className="mt-1.5 w-4 h-4 accent-brand-ink"
                            />
                            <span className="text-sm text-brand-muted leading-relaxed">
                                <strong>Optional.</strong> I'd like to receive occasional emails from
                                RealtorReferral.ca with market updates, Journal articles, and tips
                                relevant to my move. (You can unsubscribe at any time.)
                            </span>
                        </label>
                    </FieldGroup>

                    <div className="pt-2 flex flex-wrap gap-4 items-center">
                        <button
                            type="submit"
                            disabled={submitting || !consentMatching}
                            data-testid="lead-submit-button"
                            className="inline-flex items-center gap-3 bg-brand-ink text-brand-paper px-8 py-4 text-sm tracking-wide hover:bg-black transition-all hover:gap-4 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Submitting…" : "Submit & schedule"}
                            {!submitting && <span aria-hidden>→</span>}
                        </button>
                        <p className="text-xs text-brand-muted max-w-md">
                            By submitting, you agree to be contacted by RealtorReferral.ca for the purpose
                            of finding a real estate agent.
                        </p>
                    </div>
                </form>
            </section>
        </div>
    );
}

function FieldGroup({ label, children }) {
    return (
        <div className="space-y-5">
            <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold">
                {label}
            </div>
            {children}
        </div>
    );
}

function Input({ label, name, type = "text", required, defaultValue, testId }) {
    return (
        <label className="block">
            <span className="block text-sm text-brand-muted mb-2">{label}</span>
            <input
                type={type}
                name={name}
                required={required}
                defaultValue={defaultValue}
                data-testid={testId}
                className="w-full bg-transparent border-b border-brand-line py-2 text-brand-ink focus:outline-none focus:border-brand-ink transition-colors"
            />
        </label>
    );
}

function Select({ label, name, options, testId }) {
    return (
        <label className="block">
            <span className="block text-sm text-brand-muted mb-2">{label}</span>
            <select
                name={name}
                data-testid={testId}
                defaultValue=""
                className="w-full bg-transparent border-b border-brand-line py-2 text-brand-ink focus:outline-none focus:border-brand-ink transition-colors"
            >
                <option value="">— Select one —</option>
                {options.map((opt) => (
                    <option key={opt} value={opt}>
                        {opt}
                    </option>
                ))}
            </select>
        </label>
    );
}

function Textarea({ label, name, placeholder, testId }) {
    return (
        <label className="block">
            <span className="block text-sm text-brand-muted mb-2">{label}</span>
            <textarea
                name={name}
                rows={4}
                placeholder={placeholder}
                data-testid={testId}
                className="w-full bg-transparent border border-brand-line p-3 text-brand-ink focus:outline-none focus:border-brand-ink transition-colors resize-y"
            />
        </label>
    );
}
